import { CSSProperties } from 'react';

interface AdForgeLogoProps {
  size?: 'sm' | 'md' | 'lg';
  light?: boolean;
}

/* ── Size config ─────────────────────────────── */
const sizeConfig = {
  sm: {
    box: 'w-7 h-7',
    rounded: 'rounded-lg',
    roundedL: 'rounded-l-lg',
    roundedR: 'rounded-r-lg',
    sword: 'text-sm',
    text: 'text-lg',
    gap: 'gap-2',
    showCrown: false,
    showUnderline: false,
  },
  md: {
    box: 'w-8 h-8',
    rounded: 'rounded-xl',
    roundedL: 'rounded-l-xl',
    roundedR: 'rounded-r-xl',
    sword: 'text-base',
    text: 'text-xl',
    gap: 'gap-2',
    showCrown: false,
    showUnderline: true,
  },
  lg: {
    box: 'w-10 h-10',
    rounded: 'rounded-xl',
    roundedL: 'rounded-l-xl',
    roundedR: 'rounded-r-xl',
    sword: 'text-xl',
    text: 'text-2xl',
    gap: 'gap-2.5',
    showCrown: true,
    showUnderline: true,
  },
};

/* ── Sparks per size ─────────────────────────── */
const sparksIce: Record<string, CSSProperties[]> = {
  sm:  [{ left: '16%', animationDelay: '0s' }],
  md:  [{ left: '14%', animationDelay: '0s' }],
  lg:  [{ left: '6%',  animationDelay: '0s' }, { left: '24%', animationDelay: '1.1s' }],
};

const sparksFire: Record<string, CSSProperties[]> = {
  sm:  [{ left: '76%', animationDelay: '0.9s' }],
  md:  [{ left: '78%', animationDelay: '0.8s' }],
  lg:  [{ left: '63%', animationDelay: '0.5s' }, { left: '84%', animationDelay: '1.4s' }],
};

/* ── Component ───────────────────────────────── */
export default function AdForgeLogo({ size = 'lg', light = false }: AdForgeLogoProps) {
  const cfg = sizeConfig[size];
  const iceColor  = light ? '#93d2fa' : '#4fa8d0';
  const fireColor = '#d95500';
  const borderOpacity = light ? '0.32' : '0.2';

  return (
    <div className={`flex items-center ${cfg.gap}`}>

      {/* ── Badge ───────────────────────────────── */}
      <div
        className={`relative ${cfg.box} ${cfg.rounded} shrink-0`}
        style={{ overflow: 'visible' }}
      >
        {/* Obsidian base */}
        <div
          className={`absolute inset-0 ${cfg.rounded}`}
          style={{ background: '#07070f' }}
        />

        {/* Ice half — left */}
        <div
          className={`absolute top-0 left-0 w-1/2 h-full ${cfg.roundedL}`}
          style={{ background: 'linear-gradient(135deg, rgba(147,210,250,0.18) 0%, transparent 80%)' }}
        />

        {/* Fire half — right */}
        <div
          className={`absolute top-0 right-0 w-1/2 h-full ${cfg.roundedR}`}
          style={{ background: 'linear-gradient(225deg, rgba(249,115,22,0.22) 0%, transparent 80%)' }}
        />

        {/* Sword-blade center divider */}
        <div
          className="absolute top-1.5 bottom-1.5 left-1/2 w-px -translate-x-1/2 z-10"
          style={{
            background:
              'linear-gradient(to bottom, rgba(147,210,250,0.9) 0%, rgba(220,220,220,0.65) 45%, rgba(249,115,22,0.9) 100%)',
          }}
        />

        {/* Sword icon — dual color glow */}
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <i
            className={`ri-sword-fill text-stone-200 ${cfg.sword}`}
            style={{
              filter:
                'drop-shadow(0 -2px 4px rgba(147,210,250,0.7)) drop-shadow(0 2px 5px rgba(249,115,22,0.6))',
              transform: 'rotate(180deg)',
            }}
          />
        </div>

        {/* Crown — lg only */}
        {cfg.showCrown && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-30">
            <i
              className="ri-crown-fill text-amber-400 text-xs"
              style={{
                filter:
                  'drop-shadow(0 0 4px #fbbf24) drop-shadow(0 0 8px rgba(251,191,36,0.5))',
                animation: 'adforge-neon-pulse 3s ease-in-out infinite',
              }}
            />
          </div>
        )}

        {/* Ice sparks — blue override */}
        {sparksIce[size].map((s, i) => (
          <span
            key={`ice-${i}`}
            className="adforge-spark"
            style={{ ...s, background: '#93d2fa', boxShadow: '0 0 4px #93d2fa' }}
          />
        ))}

        {/* Fire sparks — orange */}
        {sparksFire[size].map((s, i) => (
          <span key={`fire-${i}`} className="adforge-spark" style={s} />
        ))}

        {/* Bottom split glow */}
        <div
          className={`absolute bottom-0 left-0 w-1/2 h-2 blur-sm ${cfg.roundedL}`}
          style={{ background: 'rgba(147,210,250,0.45)' }}
        />
        <div
          className={`absolute bottom-0 right-0 w-1/2 h-2 blur-sm ${cfg.roundedR}`}
          style={{ background: 'rgba(249,115,22,0.5)' }}
        />

        {/* Outer border — slightly brighter on dark bg */}
        <div
          className={`absolute inset-0 ${cfg.rounded}`}
          style={{ border: `1px solid rgba(200,210,230,${borderOpacity})` }}
        />
      </div>

      {/* ── Wordmark ────────────────────────────── */}
      <div className="flex flex-col leading-none gap-[3px] select-none">
        <div className="flex items-baseline gap-0">
          <span
            className={`font-black tracking-tight ${cfg.text}`}
            style={{ color: iceColor, fontFamily: 'Georgia, "Times New Roman", serif' }}
          >
            Ad
          </span>
          <span
            className={`font-black tracking-tight ${cfg.text}`}
            style={{ color: fireColor, fontFamily: 'Georgia, "Times New Roman", serif' }}
          >
            Forge
          </span>
          <span
            className="ml-1 font-extrabold tracking-widest align-super"
            style={{ fontSize: '0.37em', color: '#f59e0b' }}
          >
            AI
          </span>
        </div>

        {/* Dual-tone underline — md & lg */}
        {cfg.showUnderline && (
          <div className="flex h-[2px] w-full rounded-full overflow-hidden">
            <div
              className="flex-1"
              style={{
                background: 'linear-gradient(to right, #93d2fa, #5bafd4)',
                animation: 'adforge-slide-glow 2.2s ease-in-out infinite',
              }}
            />
            <div
              className="flex-1"
              style={{
                background: 'linear-gradient(to right, #d95500, #f97316)',
                animation: 'adforge-slide-glow 2.2s ease-in-out infinite reverse',
              }}
            />
          </div>
        )}
      </div>

    </div>
  );
}
